import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema, generateServiceSchema } from '@/utils/seo';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

/* ─── Service Images ─── */
const HOSPITALIA_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20voice%20wave%20visualization%20with%20golden%20particles%20floating%20in%20dark%20navy%20space%20soft%20glowing%20sound%20frequency%20curves%20luxurious%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20smooth%20gradients%20no%20text&width=800&height=500&seq=biz1&orientation=landscape';
const AI_CONSULTING_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20artificial%20intelligence%20neural%20network%20visualization%20with%20golden%20nodes%20and%20connections%20in%20dark%20navy%20space%20soft%20glowing%20geometric%20patterns%20luxurious%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20no%20text&width=800&height=500&seq=biz2&orientation=landscape';
const CYBER_SECURITY_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20cyber%20security%20digital%20shield%20with%20golden%20glowing%20circuit%20board%20patterns%20lock%20symbol%20dark%20navy%20background%20sophisticated%20minimal%20corporate%20aesthetic%20golden%20light%20particles%20protection%20technology%20no%20text&width=800&height=500&seq=biz3&orientation=landscape';
const AI_MARKETING_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20generative%20AI%20marketing%20digital%20data%20visualization%20golden%20flowing%20lines%20graphs%20analytics%20charts%20dark%20navy%20background%20sophisticated%20minimal%20corporate%20aesthetic%20glowing%20particles%20growth%20trend%20no%20text&width=800&height=500&seq=biz4&orientation=landscape';

/* ─── Service Detail Card ─── */
interface ServiceCardProps {
  image: string;
  icon: string;
  title: string;
  description: string;
  link: string;
  index: number;
  visible: boolean;
  onClick: () => void;
  learnMoreLabel: string;
}

function ServiceCard({ image, icon, title, description, link, index, visible, onClick, learnMoreLabel }: ServiceCardProps & { learnMoreLabel: string }) {
  return (
    <div
      className="group cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.8s ease ${0.12 + index * 0.12}s, transform 0.8s ease ${0.12 + index * 0.12}s`,
      }}
    >
      <div className="relative overflow-hidden mb-6" style={{ aspectRatio: '16/10' }}>
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, transparent 30%, rgba(10,25,47,0.7) 100%)',
          }}
        />
        {/* Gold corner */}
        <div
          className="absolute top-0 left-0 w-10 h-10 transition-all duration-500 group-hover:w-14 group-hover:h-14"
          style={{
            borderTop: '1px solid rgba(201,162,39,0.4)',
            borderLeft: '1px solid rgba(201,162,39,0.4)',
          }}
        />
        {/* Icon bottom-left */}
        <div
          className="absolute bottom-3 left-3 w-9 h-9 flex items-center justify-center cursor-pointer"
          style={{
            background: 'rgba(10,25,47,0.6)',
            border: '1px solid rgba(201,162,39,0.4)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <i className={`${icon} text-sm`} style={{ color: '#C9A227' }} />
        </div>
      </div>
      <h3
        className="text-xl mb-3 transition-colors duration-300 group-hover:text-gold-500"
        style={{
          fontFamily: "'Noto Serif JP', 'Playfair Display', serif",
          fontWeight: 500,
          color: '#1A1A1A',
        }}
      >
        {title}
      </h3>
      <p
        className="text-sm leading-relaxed mb-5"
        style={{
          color: '#3E3B34',
          fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', sans-serif",
        }}
      >
        {description}
      </p>
      <div className="flex items-center gap-4">
        <button
          onClick={onClick}
          className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer text-xs tracking-wider uppercase transition-all duration-300"
          style={{
            fontFamily: "'Noto Serif JP', serif",
            fontWeight: 500,
            padding: '10px 20px',
            border: '1px solid #C9A227',
            color: '#C9A227',
            background: 'transparent',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = '#C9A227';
            (e.currentTarget as HTMLButtonElement).style.color = '#FFFFFF';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = '#C9A227';
          }}
        >
          {learnMoreLabel}
          <i className="ri-arrow-right-line" />
        </button>
      </div>
    </div>
  );
}

/* ─── Service Detail Modal ─── */
interface ServiceDetail {
  icon: string;
  title: string;
  description: string;
  details: {
    sections: {
      title?: string;
      content?: string;
      points?: { title: string; desc: string }[];
    }[];
  };
}

function ServiceModal({ service, onClose, contactLabel, closeLabel }: { service: ServiceDetail; onClose: () => void; contactLabel: string; closeLabel: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(10, 25, 47, 0.7)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      />
      <div
        className="relative bg-white border max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        style={{
          borderColor: 'rgba(212, 207, 196, 0.5)',
          boxShadow: '0 20px 60px rgba(10, 25, 47, 0.2)',
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 bg-white border-b px-8 py-5 flex items-start justify-between z-10"
          style={{ borderColor: 'rgba(212, 207, 196, 0.4)' }}
        >
          <div className="flex items-center gap-4 pr-8">
            <div
              className="w-10 h-10 flex items-center justify-center shrink-0"
              style={{
                border: '1px solid rgba(201, 162, 39, 0.3)',
              }}
            >
              <i className={`${service.icon} text-lg`} style={{ color: '#C9A227' }} />
            </div>
            <h2
              className="text-lg leading-snug"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                color: '#1A1A1A',
              }}
            >
              {service.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center transition-colors duration-300 cursor-pointer shrink-0"
            style={{ border: '1px solid rgba(212, 207, 196, 0.5)' }}
            aria-label="Close"
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(201, 162, 39, 0.4)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212, 207, 196, 0.5)';
            }}
          >
            <i className="ri-close-line text-lg" style={{ color: '#A8A294' }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8">
          {service.details.sections.map((section, idx) => (
            <div key={idx}>
              {section.title && (
                <h3
                  className="text-sm tracking-wider uppercase mb-4"
                  style={{
                    color: '#C9A227',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {section.title}
                </h3>
              )}
              {section.content && (
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: '#3E3B34',
                    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', sans-serif",
                    lineHeight: 1.8,
                  }}
                >
                  {section.content}
                </p>
              )}
              {section.points && (
                <div className="space-y-4">
                  {section.points.map((point, pidx) => (
                    <div
                      key={pidx}
                      className="p-5 border"
                      style={{
                        borderColor: 'rgba(212, 207, 196, 0.4)',
                        background: '#FAF9F6',
                      }}
                    >
                      <p
                        className="text-sm font-medium mb-2 flex items-center gap-2"
                        style={{
                          color: '#1A1A1A',
                          fontFamily: "'Noto Serif JP', serif",
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 inline-block"
                          style={{ background: '#C9A227' }}
                        />
                        {point.title}
                      </p>
                      <p
                        className="text-sm leading-relaxed"
                        style={{
                          color: '#3E3B34',
                          fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', sans-serif",
                          lineHeight: 1.8,
                        }}
                      >
                        {point.desc}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Actions */}
          <div className="pt-4 border-t flex gap-4" style={{ borderColor: 'rgba(212, 207, 196, 0.4)' }}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer no-underline text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontWeight: 500,
                padding: '12px 28px',
                background: '#C9A227',
                color: '#0A192F',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#D4AF37';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#C9A227';
              }}
              onClick={onClose}
            >
              {contactLabel}
              <i className="ri-arrow-right-line" />
            </Link>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm tracking-wider transition-all duration-300"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                padding: '12px 24px',
                border: '1px solid rgba(212, 207, 196, 0.5)',
                color: '#A8A294',
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(201, 162, 39, 0.4)';
                (e.currentTarget as HTMLButtonElement).style.color = '#C9A227';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(212, 207, 196, 0.5)';
                (e.currentTarget as HTMLButtonElement).style.color = '#A8A294';
              }}
            >
              {closeLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function BusinessPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };
  const [selectedService, setSelectedService] = useState<number | null>(null);

  /* Scroll reveal */
  const { ref: servicesRef, visible: servicesVisible } = useScrollReveal({ threshold: 0.1 });

  useSEO({
    title: t('seo.businessTitle'),
    description: t('seo.businessDescription'),
    keywords: t('seo.businessKeywords'),
    path: '/business',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          t('seo.businessTitle'),
          t('seo.businessDescription'),
          '/business',
          getSchemaLang()
        ),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.business'), path: '/business' },
        ]),
        generateServiceSchema(
          t('business.aiConsultingTitle'),
          t('business.aiConsultingDesc')
        ),
      ],
    },
  });

  const services: ServiceDetail[] = [
    {
      icon: 'ri-robot-2-line',
      title: t('business.aiConsultingTitle'),
      description: t('business.aiConsultingDesc'),
      details: {
        sections: [
          { content: t('business.aiConsultingDetail1') },
          {
            points: [
              { title: t('business.aiConsultingPoint1'), desc: t('business.aiConsultingPoint1Desc') },
              { title: t('business.aiConsultingPoint2'), desc: t('business.aiConsultingPoint2Desc') },
              { title: t('business.aiConsultingPoint3'), desc: t('business.aiConsultingPoint3Desc') },
            ],
          },
        ],
      },
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: t('business.cyberSecurityTitle'),
      description: t('business.cyberSecurityDesc'),
      details: {
        sections: [
          { title: t('business.cyberSecurityDetail1Title'), content: t('business.cyberSecurityDetail1') },
          { title: t('business.cyberSecurityDetail2Title'), content: t('business.cyberSecurityDetail2') },
          { title: t('business.cyberSecurityDetail3Title'), content: t('business.cyberSecurityDetail3') },
        ],
      },
    },
    {
      icon: 'ri-speaker-line',
      title: t('business.hospitaliaTitle'),
      description: t('business.hospitaliaDesc'),
      details: {
        sections: [
          { title: t('business.hospitaliaDetail1Title'), content: t('business.hospitaliaDetail1') },
          { title: t('business.hospitaliaDetail2Title'), content: t('business.hospitaliaDetail2') },
          { title: t('business.hospitaliaDetail3Title'), content: t('business.hospitaliaDetail3') },
        ],
      },
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      {/* Hero */}
      <ElegantHero
        title={t('business.title')}

        tag="Business"
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('nav.business') },
        ]}
      />

      {/* Main Services */}
      <main className="py-20 md:py-28 px-6 md:px-10 max-w-[1400px] mx-auto">
        <section ref={servicesRef as React.RefObject<HTMLElement>}>
          <div className="mb-16">
            <ElegantSectionTitle
              tag="Core Services"
              title={t('business.mainServicesTitle')}
              subtitle={t('business.mainServicesSubtitle')}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-8">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                image={
                  index === 0 ? AI_CONSULTING_IMG
                  : index === 1 ? CYBER_SECURITY_IMG
                  : HOSPITALIA_IMG
                }
                icon={service.icon}
                title={service.title}
                description={service.description}
                link={'/ai-consulting-development'}
                index={index}
                visible={servicesVisible}
                onClick={() => setSelectedService(index)}
                learnMoreLabel={t('business.learnMore')}
              />
            ))}
          </div>
        </section>
      </main>

      {/* Modal */}
      {selectedService !== null && (
        <ServiceModal
          service={services[selectedService]}
          onClose={() => setSelectedService(null)}
          contactLabel={t('business.contactUs')}
          closeLabel={t('business.closeModal')}
        />
      )}

      <ElegantFooter />
    </div>
  );
}